import React from 'react';
import styled, { css } from 'styled-components';
import Button from 'src/components/form/Button';

const layout = css<{ $fullWidth?: boolean }>`
  display: ${({ $fullWidth }) => ($fullWidth ? 'flex' : 'inline-flex')};
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
`;

const Primary = styled(Button).attrs({ primary: true })<{ $fullWidth?: boolean }>`
  ${layout};
`;

const Secondary = styled(Button).attrs({ secondary: true })<{ $fullWidth?: boolean; $whiteBg?: boolean }>`
  ${layout};
  border-color: ${({ theme }) => theme.colors.gp1};

  /* Opt-in solid fill for a secondary that sits on a tinted card. The gradient label uses
     background-clip:text, so a plain background — or an inset box-shadow — paints over the clipped
     glyphs and renders the text invisible (white). Instead, layer a solid white fill (clipped to
     padding-box) beneath the gradient text layer (clipped to text), so both coexist. theme.colors.white
     is theme-aware (white in light, the dark surface in dark). The base :hover resets the background
     shorthand to the hover gradient, dropping the white layer so the gradient fill shows. */
  ${({ theme, $whiteBg }) =>
    $whiteBg &&
    css`
      background-image: ${theme.colors.gradient}, linear-gradient(${theme.colors.white}, ${theme.colors.white});
      background-clip: text, padding-box;
      -webkit-background-clip: text, padding-box;
    `}

  /* CSS stroke overrides the icon's url(#gradient) so the leading icon turns white on hover too —
     but only while enabled (a disabled button has no hover affordance). */
  &:hover:not(:disabled) svg path {
    stroke: ${({ theme }) => theme.colors.btnWhite};
  }

  /* Disabled: mute the gradient-stroked icon to match the disabled label (the base only recolours text). */
  &:disabled svg path {
    stroke: ${({ theme }) => theme.colors.cflowerBlue};
  }
`;

interface ButtonProps {
  label: string;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  onClick?: () => void;
  /** Solid (theme-aware white) fill — for a secondary button on a tinted surface. */
  whiteBg?: boolean;
}

export const PrimaryButton: React.FC<ButtonProps> = ({ label, disabled, loading, fullWidth, icon, onClick }) => (
  <Primary $fullWidth={fullWidth} disabled={disabled} loading={loading} onClick={onClick}>
    {label}
    {icon}
  </Primary>
);

export const SecondaryButton: React.FC<ButtonProps> = ({
  label,
  disabled,
  loading,
  fullWidth,
  icon,
  onClick,
  whiteBg,
}) => (
  <Secondary $fullWidth={fullWidth} $whiteBg={whiteBg} disabled={disabled} loading={loading} onClick={onClick}>
    {icon}
    {label}
  </Secondary>
);
